import React from "react";
import { useSetting, useSettingsActions } from "./SettingsStore.tsx";
import { SelectButton } from "primereact/selectbutton";
import { RadioButton } from "primereact/radiobutton";
import { CalendarMonthOptions, CalendarQuestionOptions, CalendarSpanOptions } from "../Constants.ts";
import { asksBeforeAfter } from "../CalendarGenerator.ts";
import { labelFor, withLabels } from "../Labels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * The three calendar dials (ADR 0093 §4-§6): what to ask, how far a
 * before-and-after question counts, and which month to draw.
 *
 * All basic tier, as the format's own panel is (ADR 0023 §2, as ADR 0079
 * applied it). How far to count is shown only when the sheet asks a
 * before-and-after question, as Telling Time hides its display choice for a
 * sheet with no elapsed question. The month is a native select, as the format
 * picker is (ADR 0061 §3): thirteen choices are one row.
 *
 * Reads its own slice of the settings (ADR 0081), so a change elsewhere on
 * the panel does not redraw it.
 */
export const CalendarSettings = React.memo(function CalendarSettings() {
    const { getSettings, setSettings } = useSettingsActions();
    const { question, span, month, language } = useSetting((settings) => ({
        question: settings.CalendarQuestion,
        span: settings.CalendarSpan,
        month: settings.CalendarMonth,
        language: settings.Language,
    }));
    const t = useTranslation();

    function handleQuestionChange(value: CalendarQuestionOptions) {
        // SelectButton hands back null when the selected option is clicked
        // again; a sheet has to ask something.
        if (!value) {
            return;
        }
        setSettings({ ...getSettings(), CalendarQuestion: value });
    }

    return (
        <div className="calendarSettingsContainer">
            <div className="regroupingContainer">
                <SelectButton
                    value={question}
                    onChange={(e) => handleQuestionChange(e.value)}
                    options={withLabels(Object.values(CalendarQuestionOptions), language)}
                    className="select regrouping"
                    aria-label={t('settings.questionType')}
                />
            </div>
            {asksBeforeAfter(question) && (
                <>
                    <div className="selectContainer">
                        <span className="settingLabel">{t('settings.calendarSpanLabel')}</span>
                    </div>
                    <div className="radioButtons" role="radiogroup" aria-label={t('settings.calendarSpanAria')}>
                        {Object.values(CalendarSpanOptions).map((option) => (
                            <div key={option}>
                                <RadioButton
                                    inputId={`calendarSpan-${option}`}
                                    value={option}
                                    checked={span === option}
                                    onChange={(e) => setSettings({ ...getSettings(), CalendarSpan: e.value })}
                                />
                                <label htmlFor={`calendarSpan-${option}`}>{labelFor(option, language)}</label>
                            </div>
                        ))}
                    </div>
                </>
            )}
            <div className="field">
                <label htmlFor="calendarMonth" className="settingLabel">{t('settings.calendarMonthLabel')}</label>
                <select
                    id="calendarMonth"
                    className="input"
                    value={month}
                    aria-label={t('settings.calendarMonthAria')}
                    onChange={(e) =>
                        setSettings({ ...getSettings(), CalendarMonth: e.target.value as CalendarMonthOptions })
                    }
                >
                    {Object.values(CalendarMonthOptions).map((option) => (
                        <option key={option} value={option}>{labelFor(option, language)}</option>
                    ))}
                </select>
            </div>
        </div>
    );
});

export default CalendarSettings;
